import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import {
  DwComponent,
  DwComponentType,
  DwExpressionService,
  DwMetaDataFormApi,
  DwMetaDataFormSectionStateService,
  DwMetaDataFormStateService,
  DwMetaDataService,
  DwMetaDataServiceToken,
  DwSectionReadonlyBaseComponent,
  DwUiConfigRegistryService,
  DwUIMetaDataConfig,
  DwUIMetaDataConfigToken
} from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { AppMetaData, AppMetaDataItemNames, JoinRequestEmailEntity, JoinRequestEntity } from '../../../../meta-data/app-meta-data.service';

@DwComponent({
  key: 'join-request-email-list-form-section',
  name: 'Join Request Email List Form Section',
  componentType: DwComponentType.formSection,
  parentItemName: AppMetaDataItemNames.JoinRequest,
  isGlobal: false,
})
@Component({
  selector: 'app-join-request-email-list-form-section',
  template: `
    <div class="dw-form-field">
      <div class="jr-email-form-label-row">
        <label ng-reflect-ng-class="label" class="label" for="join-request-email-list">
          Email Addresses
          <span title="Required" class="required-label">&nbsp;*</span>
          <i class="fa fa-info-circle ml-1" hidden title="undefined"></i>
        </label>
        <span class="jr-email-form-total">{{ emailTotalLabel }}</span>
      </div>

      <app-email-address-list-control
        *ngIf="!isSectionReadOnly(); else readonly"
        [(ngModel)]="emailAddresses"
        [ngModelOptions]="{ standalone: true }"
        [showLabel]="false"
        [required]="true"
        inputId="join-request-email-list"
        (ngModelChange)="emailAddressesChanged($event)">
      </app-email-address-list-control>
    </div>

    <ng-template #readonly>
      <div class="dw-chip-container" *ngIf="displayItems.length; else none">
        <div class="dw-chip" *ngFor="let item of displayItems; trackBy: trackDisplayItem">
          <span class="dw-chip-label">{{ item }}</span>
        </div>
      </div>
      <ng-template #none>
        <div>None</div>
      </ng-template>
    </ng-template>
  `,
  styles: [`
    .jr-email-form-label-row {
      align-items: center;
      display: flex;
      justify-content: space-between;
      line-height: 1.2;
      margin-bottom: var(--card-eyebrow-gap);
    }

    .jr-email-form-label-row .label {
      margin-bottom: 0;
    }

    .jr-email-form-total {
      color: #6f7785;
      font-size: 12px;
      font-weight: 600;
    }
  `]
})
export class JoinRequestEmailListFormSectionComponent extends DwSectionReadonlyBaseComponent implements OnInit, OnDestroy {
  formApi: DwMetaDataFormApi;
  joinRequestEmails?: AbstractControl;
  emailAddresses: string[] = [];
  displayItems: string[] = [];
  emailTotalLabel = '0 emails total';

  private readonly controlName = 'JoinRequestEmail';
  private readonly destroy$ = new Subject<void>();
  private readonly requiredEmailValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const emails = ((control?.value || []) as JoinRequestEmailEntity[])
      .filter(email => email._isDeleted !== true && !!(email.EmailAddress || '').trim());

    return emails.length ? null : { required: true };
  };
  private emailsHydratedFromForm = false;
  private syncingFromForm = false;

  constructor(
    dwExpressionService: DwExpressionService,
    dwUiConfigRegistryService: DwUiConfigRegistryService,
    @Inject(DwUIMetaDataConfigToken) uiMetaDataConfig: DwUIMetaDataConfig,
    @Inject(DwMetaDataServiceToken) dwMetaDataService: DwMetaDataService,
    dwFormSectionStateService: DwMetaDataFormSectionStateService,
    protected dwFormStateService: DwMetaDataFormStateService
  ) {
    super(dwExpressionService, dwFormSectionStateService, dwFormStateService, dwUiConfigRegistryService, uiMetaDataConfig, dwMetaDataService);
    this.formApi = this.dwFormStateService.state.formApi;
  }

  ngOnInit(): void {
    super.ngOnInit();

    this.joinRequestEmails = this.formGroup.get(this.controlName);

    if (!this.joinRequestEmails) {
      console.warn(`Form control ${this.controlName} not found in form group.`);
    } else {
      this.requireJoinRequestEmailControl();
    }

    this.runAfterFormDataLoaded(() => this.syncFromForm(true));

    this.formApi.getFormDataChanges()
      .pipe(debounceTime(100), takeUntil(this.destroy$))
      .subscribe(() => this.syncFromForm(false));
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  sectionReadOnlyChanged(readOnly: boolean): void {
    return;
  }

  emailAddressesChanged(emailAddresses: string[]): void {
    if (this.syncingFromForm) {
      return;
    }

    this.updateJoinRequestEmails(emailAddresses || []);
  }

  trackDisplayItem(index: number, item: string): string {
    return item;
  }

  private syncFromForm(hydrateEmails: boolean): void {
    const joinRequest = this.formApi.getFormData<JoinRequestEntity>();
    const activeEmails = this.getActiveJoinRequestEmails();
    const emailAddresses = activeEmails
      .map(email => email.EmailAddress)
      .filter((email): email is string => !!email);

    this.displayItems = emailAddresses;
    this.updateEmailTotalLabel(emailAddresses.length);

    if (!hydrateEmails || this.emailsHydratedFromForm) {
      return;
    }

    this.syncingFromForm = true;
    this.emailAddresses = this.distinctEmails([...(joinRequest?.JoinRequestEmail || activeEmails).map(email => email.EmailAddress || '')]);
    this.updateEmailTotalLabel(this.emailAddresses.length);
    this.emailsHydratedFromForm = true;
    this.syncingFromForm = false;
  }

  private updateJoinRequestEmails(emailAddresses: string[]): void {
    if (!this.joinRequestEmails) {
      console.warn(`Form control ${this.controlName} not found in form group. Email list is loaded but cannot update the form value.`);
      return;
    }

    const selectedEmails = this.distinctEmails(emailAddresses || []);
    const allJoinRequestEmails = [...((this.joinRequestEmails.value || []) as JoinRequestEmailEntity[])];
    const joinRequestId = this.formApi.getFormData<JoinRequestEntity>()?.JoinRequestId;

    allJoinRequestEmails.forEach(joinRequestEmail => {
      joinRequestEmail._isDeleted = true;
    });

    for (const emailAddress of selectedEmails) {
      let joinRequestEmail = allJoinRequestEmails.find(item => (item.EmailAddress || '').toLowerCase() === emailAddress.toLowerCase());

      if (!joinRequestEmail) {
        joinRequestEmail = AppMetaData.JoinRequestEmail.CreateEntity();
        joinRequestEmail.EmailAddress = emailAddress;
        joinRequestEmail.Accepted = false;
        allJoinRequestEmails.push(joinRequestEmail);
      }

      joinRequestEmail.EmailAddress = emailAddress;
      joinRequestEmail.JoinRequestId = joinRequestEmail.JoinRequestId || joinRequestId;
      joinRequestEmail._isDeleted = false;
    }

    this.updateJoinRequestEmailFormData(allJoinRequestEmails);
    this.joinRequestEmails.markAsTouched();
    this.displayItems = allJoinRequestEmails
      .filter(email => email._isDeleted !== true)
      .map(email => email.EmailAddress || '');
    this.emailAddresses = selectedEmails;
    this.updateEmailTotalLabel(selectedEmails.length);
  }

  private getActiveJoinRequestEmails(): JoinRequestEmailEntity[] {
    return (((this.joinRequestEmails?.value || []) as JoinRequestEmailEntity[])
      .filter(email => email._isDeleted !== true));
  }

  private distinctEmails(emails: string[]): string[] {
    const distinctValues: string[] = [];

    for (const email of emails) {
      const normalizedEmail = (email || '').trim();

      if (!normalizedEmail) {
        continue;
      }

      if (!distinctValues.some(item => item.toLowerCase() === normalizedEmail.toLowerCase())) {
        distinctValues.push(normalizedEmail);
      }
    }

    return distinctValues;
  }

  private updateEmailTotalLabel(count: number): void {
    this.emailTotalLabel = `${count} ${count === 1 ? 'email' : 'emails'} total`;
  }

  private updateJoinRequestEmailFormData(joinRequestEmails: JoinRequestEmailEntity[]): void {
    if (!this.joinRequestEmails) {
      return;
    }

    const patchData: Partial<JoinRequestEntity> = {
      JoinRequestEmail: joinRequestEmails
    };

    this.joinRequestEmails.setValue(joinRequestEmails);
    this.formApi.patchFormData(patchData);
    this.formApi.patchContextData('data', patchData);
  }

  private requireJoinRequestEmailControl(): void {
    if (!this.joinRequestEmails) {
      return;
    }

    const joinRequestEmails = this.joinRequestEmails;
    const existingValidator = joinRequestEmails.validator;

    joinRequestEmails.setValidators((control: AbstractControl): ValidationErrors | null => {
      const existingErrors = existingValidator ? existingValidator(control) : null;
      const requiredEmailError = this.requiredEmailValidator(control);
      const errors = {
        ...(existingErrors || {}),
        ...(requiredEmailError || {})
      };

      return Object.keys(errors).length ? errors : null;
    });
    joinRequestEmails.updateValueAndValidity({ emitEvent: false });
  }
}
